import apiClient from './api-client';
import { OrderPostingResult } from './order-service';
import logger from '../shared/utils/logger';

/**
 * Settings interface
 */
export interface Setting {
  value: string;
  description?: string;
  setting_id?: number;
}

/**
 * Settings map interface
 */
export interface SettingsMap {
  [key: string]: Setting;
}

/**
 * Commission settings interface
 */
export interface CommissionSettings {
  commissionRate: number;
  latePenaltyAmount: number;
  lateMarkingPenaltyAmount: number;
  monthlyTarget: number;
  targetBonusAmount: number;
  returnToStart: boolean;
  latePickupWindowMinutes: number;
  lateMarkingWindowHours: number;
}

/** Defaults used when a window setting is missing or unusable. A 0 window would
 *  mean "every order is already late", so these never fall back to 0. */
export const DEFAULT_LATE_PICKUP_WINDOW_MINUTES = 15;
export const DEFAULT_LATE_MARKING_WINDOW_HOURS = 24;

/**
 * Allowance settings interface
 */
export interface AllowanceSettings {
  petrolPrice: number;
}

/**
 * Points display settings interface
 */
export interface PointsDisplaySettings {
  pointsMode: boolean;
  pointsLabel: string;
  pointsTerm: string;
}

/**
 * Location settings interface
 */
export interface LocationSettings {
  latitude: number;
  longitude: number;
}

/**
 * Bulk settings update result from API
 */
export interface BulkSettingsUpdateResult {
  success: number;
  errors: number;
  details: Record<string, string>;
}

/**
 * Parse a setting stored as a whole number, falling back when it is missing,
 * blank, non-numeric or below 1.
 */
const parsePositiveInt = (value: string | undefined, fallback: number): number => {
  const parsed = parseInt(value ?? '', 10);
  return Number.isFinite(parsed) && parsed >= 1 ? parsed : fallback;
};

/**
 * Settings Service
 * 
 * Handles communication with the settings API endpoints
 */
class SettingsService {
  private static instance: SettingsService;
  private settingsCache: SettingsMap | null = null;
  private allSettingsFetched: boolean = false;
  
  /**
   * Get singleton instance
   */
  public static getInstance(): SettingsService {
    if (!SettingsService.instance) {
      SettingsService.instance = new SettingsService();
    }
    return SettingsService.instance;
  }
  
  /**
   * Get all settings
   * 
   * @returns Promise with all settings
   */
  public async getAllSettings(): Promise<SettingsMap> {
    try {
      if (this.allSettingsFetched && this.settingsCache) {
        return this.settingsCache;
      }

      const data = await apiClient.get<SettingsMap>('/settings');
      this.settingsCache = data;
      this.allSettingsFetched = true;
      return data;
    } catch (error: unknown) {
      logger.error('Error fetching settings:', error);
      throw error;
    }
  }
  
  /**
   * Get a specific setting by key
   * 
   * @param key The setting key
   * @returns Promise with the setting value
   */
  public async getSetting(key: string): Promise<Setting> {
    try {
      // Try to get from cache first
      if (this.settingsCache && this.settingsCache[key]) {
        return this.settingsCache[key];
      }
      
      const data = await apiClient.get<Setting>(`/settings/${key}`);
      
      // Update cache
      if (!this.settingsCache) {
        this.settingsCache = {};
      }
      this.settingsCache[key] = data;
      
      return data;
    } catch (error: unknown) {
      logger.error(`Error fetching setting ${key}:`, error);
      throw error;
    }
  }
  
  /**
   * Update a specific setting
   * 
   * @param key The setting key
   * @param value The new value
   * @param description Optional description
   * @returns Promise with the updated setting
   */
  public async updateSetting(key: string, value: string, description?: string): Promise<Setting> {
    try {
      const data: { value: string; description?: string } = { value };
      
      if (description) {
        data.description = description;
      }
      
      const result = await apiClient.put<Setting>(`/settings/${key}`, data);
      
      // Update cache
      if (!this.settingsCache) {
        this.settingsCache = {};
      }
      this.settingsCache[key] = result;
      
      return result;
    } catch (error: unknown) {
      logger.error(`Error updating setting ${key}:`, error);
      throw error;
    }
  }
  
  /**
   * Update multiple settings at once
   * 
   * @param settings Object with key-value pairs of settings
   * @returns Promise with the update result
   */
  public async updateMultipleSettings(settings: Record<string, string>): Promise<BulkSettingsUpdateResult> {
    try {
      const result = await apiClient.put<BulkSettingsUpdateResult>('/settings', settings);
      
      // Clear cache to force refresh on next get
      this.settingsCache = null;
      this.allSettingsFetched = false;
      return result;
    } catch (error: unknown) {
      logger.error('Error updating multiple settings:', error);
      throw error;
    }
  }
  
  /**
   * Get commission settings
   * 
   * @returns Promise with commission settings
   */
  public async getCommissionSettings(): Promise<CommissionSettings> {
    try {
      const settings = await this.getAllSettings();
      
      return {
        commissionRate: parseFloat(parseFloat(settings['commission_rate']?.value || '0').toFixed(2)),
        latePenaltyAmount: parseFloat(parseFloat(settings['commission_latepickup_penalty_amount']?.value || '0').toFixed(2)),
        lateMarkingPenaltyAmount: parseFloat(parseFloat(settings['commission_latemarking_penalty_amount']?.value || '0').toFixed(2)),
        monthlyTarget: parseInt(settings['commission_monthly_target']?.value || '0', 10),
        targetBonusAmount: parseFloat(parseFloat(settings['commission_target_amount']?.value || '0').toFixed(2)),
        returnToStart: settings['return_to_start']?.value === 'true',
        latePickupWindowMinutes: parsePositiveInt(
          settings['late_pickup_window_minutes']?.value,
          DEFAULT_LATE_PICKUP_WINDOW_MINUTES
        ),
        lateMarkingWindowHours: parsePositiveInt(
          settings['late_marking_window_hours']?.value,
          DEFAULT_LATE_MARKING_WINDOW_HOURS
        )
      };
    } catch (error: unknown) {
      logger.error('Error fetching commission settings:', error);
      throw error;
    }
  }
  
  /**
   * Get allowance settings
   *
   * @returns Promise with allowance settings
   */
  public async getAllowanceSettings(): Promise<AllowanceSettings> {
    try {
      const settings = await this.getAllSettings();
      return {
        petrolPrice: parseFloat(parseFloat(settings['petrol_price']?.value || '0').toFixed(2)),
      };
    } catch (error: unknown) {
      logger.error('Error fetching allowance settings:', error);
      throw error;
    }
  }

  /**
   * Update commission settings
   *
   * @param settings The commission settings
   * @returns Promise with the update result
   */
  public async updateCommissionSettings(settings: CommissionSettings): Promise<BulkSettingsUpdateResult> {
    try {
      return await this.updateMultipleSettings({
        'commission_rate': parseFloat(settings.commissionRate.toFixed(2)).toString(),
        'commission_latepickup_penalty_amount': parseFloat(settings.latePenaltyAmount.toFixed(2)).toString(),
        'commission_latemarking_penalty_amount': parseFloat(settings.lateMarkingPenaltyAmount.toFixed(2)).toString(),
        'commission_monthly_target': settings.monthlyTarget.toString(),
        'commission_target_amount': parseFloat(settings.targetBonusAmount.toFixed(2)).toString(),
        'return_to_start': settings.returnToStart ? 'true' : 'false',
        'late_pickup_window_minutes': Math.round(settings.latePickupWindowMinutes).toString(),
        'late_marking_window_hours': Math.round(settings.lateMarkingWindowHours).toString()
      });
    } catch (error: unknown) {
      logger.error('Error updating commission settings:', error);
      throw error;
    }
  }
  
  /**
   * Update allowance settings
   *
   * @param settings The allowance settings
   * @returns Promise with the update result
   */
  public async updateAllowanceSettings(settings: AllowanceSettings): Promise<{ petrol_price: number }> {
    try {
      const result = await apiClient.put<{ petrol_price: number }>('/settings/petrol-price', {
        petrol_price: parseFloat(settings.petrolPrice.toFixed(2)).toString(),
      });
      this.settingsCache = null;
      this.allSettingsFetched = false;
      return result;
    } catch (error: unknown) {
      logger.error('Error updating allowance settings:', error);
      throw error;
    }
  }

  /**
   * Get points display settings
   *
   * @returns Promise with points display settings
   */
  public async getPointsDisplaySettings(): Promise<PointsDisplaySettings> {
    try {
      const settings = await this.getAllSettings();
      return {
        pointsMode: settings['commission_points_mode']?.value === 'true',
        pointsLabel: settings['commission_points_label']?.value || 'pts',
        pointsTerm: settings['commission_points_term']?.value || 'Points',
      };
    } catch (error: unknown) {
      logger.error('Error fetching points display settings:', error);
      throw error;
    }
  }

  /**
   * Get business location settings
   *
   * @returns Promise with location settings
   */
  public async getBusinessLocation(): Promise<LocationSettings> {
    try {
      const settings = await this.getAllSettings();
      
      return {
        latitude: parseFloat(settings['business_location_latitude']?.value || '0'),
        longitude: parseFloat(settings['business_location_longitude']?.value || '0')
      };
    } catch (error: unknown) {
      logger.error('Error fetching business location:', error);
      throw error;
    }
  }
  
  /**
   * Update business location settings
   * 
   * @param location The location settings
   * @returns Promise with the update result
   */
  public async updateBusinessLocation(location: LocationSettings): Promise<BulkSettingsUpdateResult> {
    try {
      return await this.updateMultipleSettings({
        'business_location_latitude': location.latitude.toString(),
        'business_location_longitude': location.longitude.toString()
      });
    } catch (error: unknown) {
      logger.error('Error updating business location:', error);
      throw error;
    }
  }
  
  /**
   * Post orders for a specific month and year
   * 
   * @param year The year
   * @param month The month (1-12)
   * @returns Promise with the posting result
   */
  public async postOrders(year: number, month: number): Promise<OrderPostingResult> {
    try {
      const result = await apiClient.post<OrderPostingResult>('/orders/post', {
        year,
        month
      });
      
      return result;
    } catch (error: unknown) {
      logger.error('Error posting orders:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const settingsService = SettingsService.getInstance(); 